import React, { useCallback, useMemo } from 'react';

import { useLocalSearchParams } from 'expo-router';
import { PickupStoreSelector } from 'features/store/pages/pickup-store-selector';

import { isStoreAllowed } from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/is-store-allowed';
import { LoyaltyServiceOfferStoreRestrictionMode } from 'generated/graphql-gateway';
import { t } from 'state/intl/translations';

function parseParam(value?: string | string[]): string | undefined {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
}

function parseStoreRestrictionMode(
  value?: string | string[]
): LoyaltyServiceOfferStoreRestrictionMode | undefined {
  const mode = parseParam(value);
  return Object.values(LoyaltyServiceOfferStoreRestrictionMode).includes(
    mode as LoyaltyServiceOfferStoreRestrictionMode
  )
    ? (mode as LoyaltyServiceOfferStoreRestrictionMode)
    : undefined;
}

function parseAvailableStoreIds(value?: string | string[]): string[] {
  return (parseParam(value) ?? '')
    .split(',')
    .map(storeId => storeId.trim())
    .filter(Boolean);
}

/**
 * Minimal store locator for menu and offer unavailable Tap-here flows.
 * Offer flows filter the results using loyalty-service store restrictions.
 * Menu flows retain their existing current-store unavailable treatment.
 */
export default function MinimalStoreLocator() {
  const {
    offerEngineId,
    unavailableItemId,
    storeRestrictionMode,
    availableStoreIds,
  } = useLocalSearchParams<{
    offerEngineId?: string | string[];
    unavailableItemId?: string | string[];
    storeRestrictionMode?: string | string[];
    availableStoreIds?: string | string[];
  }>();
  const parsedOfferEngineId = parseParam(offerEngineId);
  const isOfferFlow = Boolean(parsedOfferEngineId);
  // Only the feed's unavailable card sends `unavailableItemId`, so its presence
  // means the guest opened this selector on purpose from that card.
  const openedFromUnavailableCard = Boolean(parseParam(unavailableItemId));
  const restrictionMode = parseStoreRestrictionMode(storeRestrictionMode);
  const eligibleStoreIds = useMemo(() => parseAvailableStoreIds(availableStoreIds), [
    availableStoreIds,
  ]);
  const isStoreEligible = useCallback(
    (store: { number?: string | null }) =>
      isStoreAllowed(store.number, restrictionMode, eligibleStoreIds),
    [eligibleStoreIds, restrictionMode]
  );

  return (
    <PickupStoreSelector
      nearbyOnly
      isOffersSelection
      headerText={t('selectLocation')}
      pinCurrentAsUnavailable={!isOfferFlow && openedFromUnavailableCard}
      isStoreEligible={isOfferFlow ? isStoreEligible : undefined}
      storeFilter={isOfferFlow ? isStoreEligible : undefined}
      noEligibleStoresMessage={isOfferFlow ? t('noAvailableRestaurantsForOffer') : undefined}
      ineligibleStoreMessage={t('notAvailableInThisRestaurant')}
    />
  );
}
